import React from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Typography,
  Checkbox,
  ListItemText,
  InputAdornment,
  IconButton,
  alpha,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { COLORS, FONT_SIZES, FONT_WEIGHTS } from '../../theme/constants';

/**
 * StyledSelect — uses global theme overrides (MuiOutlinedInput, MuiSelect, etc.).
 */
const StyledSelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
  fullWidth = true,
  size = 'small',
  required = false,
  error = false,
  helperText,
  multiple = false,
  showCheckboxes = false,
  clearable = false,
  onClear,
  renderValue,
  placeholderWhenEmpty,
  sx = {},
  ...props
}) => {
  const labelId = label
    ? `${label.replace(/\s+/g, '-').toLowerCase()}-label`
    : undefined;

  return (
    <FormControl fullWidth={fullWidth} size={size} error={error} required={required}>
      {label && (
        <InputLabel
          id={labelId}
          sx={{
            fontSize: FONT_SIZES.body2,
            color: 'text.secondary',
            fontWeight: FONT_WEIGHTS.medium,
            '&.Mui-focused': {
              color: 'primary.main',
            },
          }}
        >
          {label}
        </InputLabel>
      )}
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={onChange}
        multiple={multiple}
        disabled={disabled}
        displayEmpty={!!placeholder && !label}
        renderValue={
          renderValue ||
          (multiple
            ? selected => {
                const selectedValues = Array.isArray(selected) ? selected : [];
                if (selectedValues.length === 0) {
                  return (
                    <Typography variant="body2" color="text.disabled">
                      {placeholderWhenEmpty || placeholder || ''}
                    </Typography>
                  );
                }
                return options
                  .filter(option => selectedValues.includes(option.value))
                  .map(option => option.label)
                  .join(', ');
              }
            : undefined)
        }
        endAdornment={
          clearable &&
          multiple &&
          Array.isArray(value) &&
          value.length > 0 &&
          typeof onClear === 'function' ? (
            <InputAdornment position="end" sx={{ mr: 3 }}>
              <IconButton
                size="small"
                aria-label="Clear selected values"
                onMouseDown={event => event.preventDefault()}
                onClick={event => {
                  event.stopPropagation();
                  onClear();
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null
        }
        sx={{
          '&.MuiOutlinedInput-root': {
            backgroundColor: COLORS.background.paper,
            fontSize: FONT_SIZES.body1,
            transition: 'all 0.2s ease',

            '& fieldset': {
              borderColor: COLORS.border.light,
              borderWidth: 1.5,
              transition: 'all 0.2s ease',
            },

            '&:hover': {
              '& fieldset': {
                borderColor: COLORS.border.main,
              },
            },

            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(COLORS.primary.main, 0.12)}`,
              '& fieldset': {
                borderColor: COLORS.primary.main,
                borderWidth: 2,
              },
            },

            '&.Mui-error': {
              '& fieldset': {
                borderColor: COLORS.error.main,
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${alpha(COLORS.error.main, 0.12)}`,
              },
            },

            '&.Mui-disabled': {
              backgroundColor: COLORS.background.default,
              '& fieldset': {
                borderColor: COLORS.border.light,
              },
            },
          },
          '& .MuiSelect-select': {
            py: 1.1,
          },
          ...sx,
        }}
        {...props}
      >
        {placeholder && !label && (
          <MenuItem value="" disabled>
            <Typography variant="body2" color="text.disabled" component="span">
              {placeholder}
            </Typography>
          </MenuItem>
        )}
        {options.map(option => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {showCheckboxes && multiple ? (
              <>
                <Checkbox checked={Array.isArray(value) && value.includes(option.value)} />
                <ListItemText primary={option.label} />
              </>
            ) : (
              option.label
            )}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText
          sx={{
            fontSize: FONT_SIZES.caption,
            marginLeft: 0,
            marginTop: 0.5,
          }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default StyledSelect;
